import { useState, useEffect, useRef } from "react";
import { ArrowRight, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../../utils/Images";
import { Link, useNavigate, useLocation } from "react-router-dom";

export default function VerifyCode() {
  const [code, setCode] = useState(["", "", "", ""]);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { method, contact } = location.state || {};

  // Check if required state is available
  useEffect(() => {
    // This effect can be used for any initialization logic
    // The routing system will handle redirects if needed
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleInputChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input
      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
      setCode(newCode);
      
      // Focus the next empty input or the last one
      const nextEmptyIndex = newCode.findIndex((digit, i) => !digit && i < 4);
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[3]?.focus();
      }
    }
  };

  const handleVerifyCode = () => {
    const fullCode = code.join("");
    if (fullCode.length === 4) {
      // Navigate to reset password page
      navigate("/forgot-password/reset", { 
        state: { 
          method, 
          contact,
          verificationCode: fullCode
        } 
      });
    }
  };

  const handleResendCode = () => {
    if (canResend) {
      setCountdown(30);
      setCanResend(false);
      setCode(["", "", "", ""]);
      inputRefs.current[0]?.focus();
      // Here you would typically make an API call to resend the code
    }
  };

  const isCodeComplete = code.every(digit => digit !== "");

  return (
    <div className="relative min-h-screen">
      <img 
        src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop" 
        alt="bg" 
        className="object-cover absolute inset-0 w-full h-full" 
      />
      <div className="absolute inset-0 bg-black/60" />
      
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex relative z-10 justify-center items-start pt-10"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>
      
      <div className="flex relative z-10 justify-center items-center p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h2 className="text-lg font-semibold text-center">Verify Code</h2>
            <p className="mt-1 text-xs text-center text-gray-500">
              Enter the 4-digit code sent to your {method}
            </p>
            <p className="mt-1 text-xs font-medium text-center text-emerald-600">
              {contact}
            </p>
          </motion.div>

          <div className="mt-6 space-y-6">
            {/* Code Input Circles */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
              className="flex gap-3 justify-center"
            >
              {code.map((digit, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                  className="relative group"
                >
                  <div className="relative rounded-full p-[2px] bg-gradient-to-br from-emerald-400/40 via-transparent to-emerald-400/40 transition-all duration-300 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                    <motion.input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      whileFocus={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                      className="w-14 h-14 text-lg font-bold text-center rounded-full border-0 transition-all duration-300 outline-none bg-white/95 focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none focus:bg-white focus:text-emerald-700"
                      maxLength="1"
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Resend Code */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.3 }}
              className="text-center"
            >
              <AnimatePresence mode="wait">
                {canResend ? (
                  <motion.button
                    key="resend"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleResendCode}
                    className="inline-flex gap-2 items-center text-xs font-medium text-emerald-600 transition-colors hover:text-emerald-700"
                  >
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                      <RotateCcw size={14} />
                    </motion.div>
                    Resend Code
                  </motion.button>
                ) : (
                  <motion.p 
                    key="countdown"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="text-xs text-gray-500"
                  >
                    Resend code in 
                    <motion.span
                      key={countdown}
                      initial={{ scale: 1.2, color: "#10b981" }}
                      animate={{ scale: 1, color: "#6b7280" }}
                      transition={{ duration: 0.2 }}
                      className="font-medium"
                    >
                      {countdown}
                    </motion.span>
                    s
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Verify Button */}
            <motion.button
              onClick={handleVerifyCode}
              disabled={!isCodeComplete}
              whileHover={{ scale: isCodeComplete ? 1.02 : 1 }}
              whileTap={{ scale: isCodeComplete ? 0.98 : 1 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.4 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                Verify Code 
                <motion.div
                  animate={isCodeComplete ? { x: [0, 3, 0] } : { x: 0 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ArrowRight size={16} />
                </motion.div>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>

            {/* Back Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.3 }}
            >
              <Link 
                to="/forgot-password" 
                className="block text-xs text-center text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Back to Enter Contact
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
